import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import apiClient from "@/utils/apiClient";
import DOMPurify from "dompurify";
import { Megaphone, Trash2, Plus, X, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import FileUpload from "@/components/FileUpload";
import AttachmentList from "@/components/AttachmentList";
import RichTextEditor from "@/components/RichTextEditor";
import TemplateSelector from "@/components/TemplateSelector";

export default function AdminAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", category: "general", send_email: false, attachments: [] });
  const [saving, setSaving] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [editorKey, setEditorKey] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [annRes, emailRes] = await Promise.all([
        apiClient.get('/admin/announcements'),
        apiClient.get('/admin/email/status')
      ]);
      setAnnouncements(annRes.data.announcements);
      setTotal(annRes.data.total);
      setEmailStatus(emailRes.data);
    } catch (err) { console.error('Failed to fetch announcements:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post('/admin/announcements', form);
      setForm({ title: "", content: "", category: "general", send_email: false, attachments: [] });
      setEditorKey(k => k + 1);
      setShowForm(false);
      fetchData();
    } catch (err) { /* silent */ }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try { await apiClient.delete(`/admin/announcements/${id}`); fetchData(); }
    catch (err) { /* silent */ }
  };

  const categories = ["general", "wellness_tip", "event", "system"];
  const catColors = { general: "bg-blue-500/10 text-blue-400", wellness_tip: "bg-emerald-500/10 text-emerald-400", event: "bg-violet-500/10 text-violet-400", system: "bg-amber-500/10 text-amber-400" };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-announcements">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-white mb-1">Announcements</h1>
            <p className="text-white/40 text-sm">{total} announcements</p>
          </div>
          <Button onClick={() => setShowForm(!showForm)} className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl gap-2" data-testid="new-announcement-btn">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "Cancel" : "New Announcement"}
          </Button>
        </div>

        {showForm && (
          <form onSubmit={handleCreate} className="bg-[#161821] border border-white/5 rounded-2xl p-6 mb-6 space-y-4" data-testid="announcement-form">
            {!form.content && <TemplateSelector onSelect={(t) => { setForm({...form, title: t.name, content: t.content}); setEditorKey(k => k + 1); }} />}
            <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Announcement title..." className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" required data-testid="announcement-title" />
            <RichTextEditor key={editorKey} content={form.content} onChange={(html) => setForm(prev => ({...prev, content: html}))} placeholder="Write your announcement..." />
            <div className="flex items-center gap-3 flex-wrap">
              <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="bg-[#0f1117] border border-white/10 text-white rounded-xl px-3 py-2" data-testid="announcement-category">
                {categories.map(c => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
              </select>
              {emailStatus?.configured && (
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="announcement-send-email-toggle">
                  <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({...form, send_email: e.target.checked})} className="w-4 h-4 rounded accent-amber-500" />
                  <Mail className="w-4 h-4 text-amber-400/70" />
                  <span className="text-white/60 text-sm">Email all users</span>
                </label>
              )}
              <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl gap-2" data-testid="announcement-submit">
                {form.send_email ? <Send className="w-3.5 h-3.5" /> : null}
                {saving ? "Publishing..." : form.send_email ? "Publish & Email" : "Publish"}
              </Button>
            </div>
            <FileUpload files={form.attachments} onChange={(files) => setForm({...form, attachments: files})} maxFiles={5} />
          </form>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>
        ) : announcements.length > 0 ? (
          <div className="space-y-3">
            {announcements.map(a => (
              <div key={a.announcement_id} className="bg-[#161821] border border-white/5 rounded-2xl p-5 flex items-start justify-between gap-4" data-testid={`ann-${a.announcement_id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${catColors[a.category] || catColors.general}`}>{a.category.replace("_", " ")}</span>
                    <span className="text-white/30 text-xs">{new Date(a.created_at).toLocaleDateString()}</span>
                  </div>
                  <h3 className="text-white font-medium mb-1">{a.title}</h3>
                  <div className="text-white/50 text-sm line-clamp-3 prose prose-invert prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.content) }} />
                  <AttachmentList attachments={a.attachments} />
                </div>
                <button onClick={() => handleDelete(a.announcement_id)} className="p-2 rounded-xl hover:bg-red-500/10 text-white/20 hover:text-red-400 transition-colors" data-testid={`del-ann-${a.announcement_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#161821] border border-white/5 rounded-2xl p-12 text-center">
            <Megaphone className="w-12 h-12 text-white/10 mx-auto mb-4" />
            <p className="text-white/30">No announcements yet</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
